import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useController } from 'react-hook-form';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { classNames } from 'primereact/utils';
import { CatalogoService } from '../../../service/CatalogoService';
import { ActividadEconomicaModel } from '../../../models/administracion/ActividadEconomicaModel';
import { useLazyParams } from '../../../hooks/useLazyParams';
import { TablaDinamica } from '../../TablaDinamica';
import { Tag } from 'primereact/tag';
import { useGlobalToast } from '../../../context/ToastContext';
import * as generic from '../../Generico';

// Componente auxiliar para el Tooltip personalizado con Portal
const CustomTooltip = ({ text, children }) => {
    const [pos, setPos] = useState(null);

    const handleMouseEnter = (e) => {
        if (!text || typeof text !== 'string' || !text.trim()) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPos({
            top: rect.top - 6,
            left: rect.left + rect.width / 2
        });
    };

    const handleMouseLeave = () => setPos(null);

    return (
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="w-full flex align-items-center" style={{ minWidth: 0 }}>
            {children}
            {pos && text && createPortal(
                <div className="custom-portal-tooltip" style={{ top: `${pos.top}px`, left: `${pos.left}px`, transform: 'translate(-50%, -100%)' }}>
                    {text}
                </div>,
                document.body
            )}
        </div>
    );
};

export const InputActividadEconomica = ({ name, control, modelDef, disabled, col = 12 }) => {
    const { toast } = useGlobalToast();
    const { field, fieldState } = useController({
        name, control,
        rules: {
            required: modelDef.required ? `${modelDef.required}` : false,
            validate: (value) => {
                if (!value && !modelDef.required) return true;
                if (!value?.cat_id) {
                    return 'Debe seleccionar una actividad del listado';
                }
                return true;
            }
        }
    });

    const [suggestions, setSuggestions] = useState([]);
    const [mostrarModal, setMostrarModal] = useState(false);
    const catalogoService = useMemo(() => new CatalogoService(), []);
    const omodel = useMemo(() => new ActividadEconomicaModel(), []);
    const tablaEstado = useLazyParams({ modelo: omodel.model });

    // Si hay un valor cargado (desde BD o seleccionado), lo metemos en 'suggestions'. Así, cuando PrimeReact haga onBlur, encontrará el elemento y no borrará el input.
    useEffect(() => {
        if (field.value && typeof field.value === 'object' && field.value.cat_id) {
            setSuggestions([field.value]);
        }
    }, [field.value]);

    const buscarCiiu = (event) => {
        const query = event.query;
        if (!query.trim().length) {
            // Si borra todo, podemos restablecer al valor actual si existe, o dejar vacío
            setSuggestions(field.value ? [field.value] : []);
            return;
        }
        catalogoService.buscarActividadCiiu(query.toLowerCase()).then(res => {
            setSuggestions(res.data);
        }).catch(error => {
            generic.funcSistemaError(error, toast);
        });
    };

    useEffect(() => {
        if (mostrarModal) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams, mostrarModal]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        const _lazyParams = { ...tablaEstado.lazyParams };
        catalogoService.getActividadesCiiu({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
        }).catch(error => {
            generic.funcSistemaError(error, toast);
        }).finally(() => {
            tablaEstado.setLoading(false)
        });
    };

    const onSeleccionarActividad = (rowData) => {
        field.onChange(rowData);
        tablaEstado.resetLazyParams();
        setMostrarModal(false);
    };

    const ciiuItemTemplate = (item) => {
        return (
            <CustomTooltip text={item.nombre}>
                <div className="flex align-items-center gap-3 py-1 px-1 border-round w-full" style={{ overflow: 'hidden' }}>
                    <Tag value={item.nemonico || ''} severity="info" style={{ minWidth: '85px', justifyContent: 'center' }} className="font-mono text-xs shadow-1 flex-shrink-0" />
                    <span className="font-medium text-800 text-sm line-height-2"
                        style={{ display: 'block', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}>
                        {item.nombre}
                    </span>
                </div>
            </CustomTooltip>
        );
    };

    const accionesTemplate = (rowData) => {
        return (
            <Button icon="pi pi-check" label="Elegir" type="button" className="p-button-sm p-button-rounded p-button-outlined p-button-success" onClick={() => onSeleccionarActividad(rowData)} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Elegir" body={accionesTemplate} style={{ width: '5rem', textAlign: 'center' }} frozen alignFrozen="right" />
    );

    // Solo se enviará el texto del tooltip si 'field.value' es un objeto seleccionado explícitamente (con propiedad nombre)
    const valorInputTooltip = (field.value && typeof field.value === 'object' && field.value.nombre) ? field.value.nombre : '';

    return (
        <div className={`field col-${col}`}>
            <label htmlFor={name} className={classNames("font-medium text-800 text-sm", { 'p-error': fieldState.error })}>
                {modelDef.header} {modelDef.required && '*'}
            </label>

            <div className="p-inputgroup w-full" style={{ minWidth: 0 }}>
                <CustomTooltip text={valorInputTooltip}>
                    <AutoComplete id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)} suggestions={suggestions}
                        completeMethod={buscarCiiu} field="nombre" itemTemplate={ciiuItemTemplate} dropdownAriaLabel="Seleccione"
                        className={classNames('w-full', { 'p-invalid': fieldState.error })} style={{ flex: '1 1 auto', minWidth: 0 }} inputStyle={{ width: '100%', textOverflow: 'ellipsis' }}
                        panelStyle={{ width: '100%', maxWidth: '500px' }} disabled={disabled}
                        placeholder='Ingrese un código o nombre de la actividad y seleccione...' forceSelection />
                </CustomTooltip>
                <Button icon="pi pi-search-plus" className="p-button-primary flex-shrink-0" type="button" onClick={() => setMostrarModal(true)}
                    disabled={disabled} tooltip="Abrir explorador avanzado" tooltipOptions={{ position: 'left' }} />
            </div>
            {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}

            <small className="block mt-1 text-blue-600 font-medium flex align-items-center gap-1" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                <i className="pi pi-info-circle text-xs" />
                <span>Para buscar una actividad económica, digite el <strong>código</strong> o el <strong>nombre</strong>. Para explorar el listado completo, presione el botón de la lupa.</span>
            </small>

            <Dialog header="Explorador de Actividades Económicas" visible={mostrarModal} style={{ width: '80vw', maxWidth: '1200px' }} maximizable modal onHide={() => setMostrarModal(false)} className="dialog-personalizado">
                <div className="pt-2">
                    <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="cat_id" acciones={ColumnaAcciones} />
                </div>
            </Dialog>
        </div>
    );
};